import { Decimal as DecimalBase } from 'decimal.js';
/**
 * The branded Money value object required by DECISION.md §2.1.
 *
 * TypeScript has no native decimal, so money NEVER exists as a JS number in
 * this codebase: NUMERIC columns come back from Postgres as strings, become
 * Money here, and every operation runs on decimal.js under the hood. The
 * companion ESLint rule (`@palaccount/money/eslint-plugin`) fails the build on
 * raw `+ - * /` in money-handling modules, which converts the decimal gap from
 * a discipline problem into a compile-time error.
 *
 * Rounding follows PART 18:
 *   N-6  round to 2 decimals, HALF-UP, via one shared helper (that helper is
 *        `Money.prototype.rounded` / `round2` below — nothing else may round).
 *   N-7  a total made of displayed parts rounds each part FIRST, then adds the
 *        rounded parts, so the printed parts always sum to the printed total.
 *   N-13 comparisons against zero use a tolerance, never an exact test.
 *
 * PART 4 says the same things under its own numbers, cited here at the one
 * enforcement point: GEN-96 (money is stored with exactly two decimals —
 * NUMERIC(15,2) columns end to end, `toFixed2` at every boundary), GEN-106
 * (rounding is half AWAY FROM ZERO — decimal.js ROUND_HALF_UP is exactly
 * that: 0.125 → 0.13 and −0.125 → −0.13), and the GEN-103/GEN-104/GEN-105
 * sum-of-rounded-parts ladder is N-7 by another name, applied in
 * invoices/totals.ts and the POS till.
 */
/** A local Decimal clone so our config never leaks into other decimal.js users. */
const Decimal = DecimalBase.clone({
    // Plenty for NUMERIC(15,2) arithmetic including intermediate products.
    precision: 40,
    rounding: DecimalBase.ROUND_HALF_UP,
});
/**
 * N-13 — a journal entry balances when total debits and total credits differ
 * by less than half a cent (also BR-FIN-02-01, "within half a cent").
 */
export const JOURNAL_BALANCE_TOLERANCE = '0.005';
/**
 * N-13 / F-9 — a document is fully paid when its balance due is at or below a
 * ten-thousandth, never when it is exactly zero.
 */
export const FULLY_PAID_TOLERANCE = '0.0001';
export class MoneyError extends Error {
}
export class CurrencyMismatchError extends MoneyError {
    constructor(left, right) {
        super(`Cannot combine ${left} with ${right}: money arithmetic is currency-tagged`);
        this.name = 'CurrencyMismatchError';
    }
}
export class InvalidAmountError extends MoneyError {
    constructor(raw) {
        super(typeof raw === 'number'
            ? `Money must be constructed from a string, never a JS number (got ${String(raw)}). ` +
                'NUMERIC columns must reach the application as strings (DECISION.md §2.3).'
            : `Not a decimal amount: ${String(raw)}`);
        this.name = 'InvalidAmountError';
    }
}
const AMOUNT_PATTERN = /^[+-]?\d+(\.\d+)?$/;
const CURRENCY_PATTERN = /^[A-Z]{3}$/;
export class Money {
    value;
    currency;
    constructor(value, 
    /** ISO-4217 style tag, e.g. 'USD'. Arithmetic across tags throws. */
    currency) {
        this.value = value;
        this.currency = currency;
        Object.freeze(this);
    }
    /**
     * The only door in. `amount` is a string on purpose: accepting a number here
     * would invite `Money.of(0.1 + 0.2)`, which is exactly the bug this class
     * exists to make impossible. Postgres NUMERIC arrives as a string already.
     */
    static of(amount, currency) {
        if (typeof amount !== 'string' || !AMOUNT_PATTERN.test(amount.trim())) {
            throw new InvalidAmountError(amount);
        }
        if (typeof currency !== 'string' || !CURRENCY_PATTERN.test(currency)) {
            throw new MoneyError(`Not a currency tag: ${String(currency)}`);
        }
        return new Money(new Decimal(amount.trim()), currency);
    }
    static zero(currency) {
        return Money.of('0', currency);
    }
    assertSameCurrency(other) {
        if (other.currency !== this.currency) {
            throw new CurrencyMismatchError(this.currency, other.currency);
        }
    }
    plus(other) {
        this.assertSameCurrency(other);
        return new Money(this.value.plus(other.value), this.currency);
    }
    minus(other) {
        this.assertSameCurrency(other);
        return new Money(this.value.minus(other.value), this.currency);
    }
    /** Multiply by a scalar (quantity, rate, percentage ÷ 100) given as a string. */
    times(factor) {
        if (typeof factor !== 'string' || !AMOUNT_PATTERN.test(factor.trim())) {
            throw new InvalidAmountError(factor);
        }
        return new Money(this.value.times(new Decimal(factor.trim())), this.currency);
    }
    /** Divide by a scalar given as a string. */
    dividedBy(divisor) {
        if (typeof divisor !== 'string' || !AMOUNT_PATTERN.test(divisor.trim())) {
            throw new InvalidAmountError(divisor);
        }
        const d = new Decimal(divisor.trim());
        if (d.isZero())
            throw new MoneyError('Division of money by zero');
        return new Money(this.value.dividedBy(d), this.currency);
    }
    negated() {
        return new Money(this.value.negated(), this.currency);
    }
    abs() {
        return new Money(this.value.abs(), this.currency);
    }
    /**
     * N-6 — THE shared rounding helper: 2 decimals, half-up. Every figure that
     * is stored or shown goes through here exactly once; nothing else rounds.
     */
    rounded() {
        return new Money(this.value.toDecimalPlaces(2, Decimal.ROUND_HALF_UP), this.currency);
    }
    isNegative() {
        return this.value.isNegative() && !this.value.isZero();
    }
    isPositive() {
        return this.value.greaterThan(0);
    }
    /** N-13 — zero within a tolerance, never an exact test. */
    isZero(tolerance = '0') {
        return this.value.abs().lessThanOrEqualTo(new Decimal(tolerance));
    }
    /** N-13 — |this − other| < tolerance. Used for the balance test (0.005). */
    differsByLessThan(other, tolerance) {
        this.assertSameCurrency(other);
        return this.value.minus(other.value).abs().lessThan(new Decimal(tolerance));
    }
    equals(other) {
        return this.currency === other.currency && this.value.equals(other.value);
    }
    greaterThan(other) {
        this.assertSameCurrency(other);
        return this.value.greaterThan(other.value);
    }
    lessThan(other) {
        this.assertSameCurrency(other);
        return this.value.lessThan(other.value);
    }
    /**
     * The storage/display form: exactly 2 decimals (N-1), no grouping, minus
     * sign in front. This is what goes into a NUMERIC(…,2) column. Rounds
     * half-up if the value carries more precision (N-6).
     */
    toFixed2() {
        return this.value.toFixed(2, Decimal.ROUND_HALF_UP);
    }
    /**
     * The same half-up rounding at another precision, for the decimals that are
     * not money. N-4: a QUANTITY carries three decimals, and PART 9's
     * `qty × (1 + scrap ÷ 100)` is a quantity — the value object is still the
     * right tool (it is the only decimal type in the codebase) but 2 dp is the
     * wrong precision for it. Money amounts keep using `rounded()`/`toFixed2()`;
     * nothing else may round a money figure.
     */
    toFixed(dp) {
        if (!Number.isInteger(dp) || dp < 0 || dp > 20) {
            throw new MoneyError(`Not a decimal precision: ${String(dp)}`);
        }
        return this.value.toFixed(dp, Decimal.ROUND_HALF_UP);
    }
    /** Full-precision decimal string, for intermediate values and debugging. */
    toDecimalString() {
        return this.value.toString();
    }
    toString() {
        return `${this.currency} ${this.toFixed2()}`;
    }
    toJSON() {
        return { amount: this.toFixed2(), currency: this.currency };
    }
}
/**
 * N-6 as a bare-string convenience for code that has not yet lifted a NUMERIC
 * string into Money. Same helper, same rule: 2 decimals, half-up.
 */
export function round2(amount) {
    return Money.of(amount, 'USD').rounded().toFixed2();
}
/**
 * N-7 — round each part first, then add the rounded parts, so the printed
 * parts always add up to the printed total.
 */
export function sumRounded(parts, currency) {
    let total = Money.zero(currency);
    for (const part of parts) {
        total = total.plus(part.rounded());
    }
    return total;
}
//# sourceMappingURL=money.js.map